import "../styles/Dashboard.css";
import Card from "../components/Card";
import ToolCard from "../components/ToolCard";
import TextBox from "../components/TextBox";
import { Link, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import api from "../api/api";
import Modal from "../components/Modal"; // NEW
import {
  Calendar,
  TrendingUp,
  Target,
  Award,
  User,
  Sun,
  Cloud,
  CloudRain,
  Wind,
  Heart,
  BookOpen,
  Phone,
  AlertCircle,
} from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();
  const username = localStorage.getItem("username") || "User";

  const [currentDate, setCurrentDate] = useState("");
  const [temperature, setTemperature] = useState("");

  const [dayEntry, setDayEntry] = useState("");
  const [mood, setMood] = useState(50); // 0-100 scale slider
  const [tags, setTags] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dashboardSummary, setDashboardSummary] = useState({
    weekly_average: 0,
    total_checkins: 0,
    current_streak: 0,
    wellness_score: "N/A",
    today_stress: null,
  });

  useEffect(() => {
    async function fetchDashboardSummary() {
      try {
        const response = await api.get("/dashboard/summary/");
        setDashboardSummary(response.data);
      } catch (err) {
        console.error("Failed to fetch dashboard summary", err);
      }
    }
    fetchDashboardSummary();
  }, []);

  const [isModalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");

  const availableTags = [
    "Work",
    "Sleep",
    "Food",
    "Social",
    "Family",
    "Health",
    "Finances",
    "Relationships",
    "Exercise",
    "Travel",
    "Weather",
  ];

  const toggleTag = (tag) => {
    setTags((prevTags) =>
      prevTags.includes(tag)
        ? prevTags.filter((t) => t !== tag)
        : [...prevTags, tag]
    );
  };

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const payload = {
        description: dayEntry,
        mood: mood,
        tags: tags,
      };
      const response = await api.post("/dashboard/checkin/", payload);

      setAnalysisResult(response.data.predicted_stress);

      // Refresh summary stats immediately after analysis
      const summaryResponse = await api.get("/dashboard/summary/");
      setDashboardSummary(summaryResponse.data);
    } catch (error) {
      console.error("Error analyzing day:", error);
      setAnalysisResult("Failed to analyze your day. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const roundedStress =
    analysisResult !== null && typeof analysisResult === "number"
      ? Number(analysisResult.toFixed(2))
      : null;

  const stressPercent = roundedStress !== null ? (roundedStress / 10) * 100 : 0;

  const handleLogout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    navigate("/login");
  };

  const openToolModal = (tool) => {
    let content = "";
    if (tool === "Breathing Exercises") {
      content = (
        <div>
          <ul>
            <li>Inhale deeply for 4 seconds</li>
            <li>Hold your breath for 7 seconds</li>
            <li>Exhale slowly for 8 seconds</li>
            <li>Repeat for 5 minutes</li>
          </ul>
          <p>
            For a guided video, check out this{" "}
            <a
              href="https://www.youtube.com/watch?v=I-SFdhVwrVA"
              target="_blank"
              rel="noopener noreferrer"
            >
              5-minute breathing exercise on YouTube
            </a>
            .
          </p>
        </div>
      );
    } else if (tool === "Quick Meditation") {
      content = (
        <div>
          <h4>Meditation: Dos and Don'ts</h4>
          <ul>
            <li>
              <strong>Do</strong> find a quiet, comfortable space free of
              distractions.
            </li>
            <li>
              <strong>Do</strong> focus on your breath or a mantra to center
              your mind.
            </li>
            <li>
              <strong>Do</strong> start with short sessions and gradually
              increase duration.
            </li>
            <li>
              <strong>Don't</strong> expect immediate results; meditation is a
              practice.
            </li>
            <li>
              <strong>Don't</strong> force thoughts away—acknowledge them and
              gently return focus.
            </li>
          </ul>

          <h4>Techniques</h4>
          <p>
            <strong>Mindfulness Meditation:</strong> Observe your thoughts and
            sensations without judgment.
          </p>
          <p>
            <strong>Guided Meditation:</strong> Follow along with an
            instructor’s voice or app.
          </p>
          <p>
            <strong>Body Scan:</strong> Slowly focus attention on each part of
            your body to release tension.
          </p>

          <h4>Tips</h4>
          <p>
            Practice daily, even if just for a few minutes, to build consistency
            and reap benefits.
          </p>
        </div>
      );
    } else if (tool === "Stress Tips") {
      content = (
        <div>
          <h4>5 Stress Reduction Tips</h4>
          <ol>
            <li>
              <strong>Take short breaks:</strong> Step away from your work to
              recharge your mind.
            </li>
            <li>
              <strong>Stay hydrated:</strong> Drinking water can improve focus
              and reduce fatigue.
            </li>
            <li>
              <strong>Physical activity:</strong> Even a short walk can boost
              endorphins and relieve tension.
            </li>
            <li>
              <strong>Deep breathing:</strong> Helps activate your
              parasympathetic nervous system to calm stress.
            </li>
            <li>
              <strong>Limit multitasking:</strong> Focus on one task at a time
              to improve productivity and reduce overwhelm.
            </li>
          </ol>
        </div>
      );
    } else if (tool === "Emergency Help") {
      content = (
        <div>
          <h4>Emergency Contacts</h4>
          <p>
            If you are in crisis, please reach out immediately to one of the
            following resources:
          </p>
          <ul>
            <li>
              <strong>National Suicide Prevention Lifeline:</strong>{" "}
              1-800-273-8255
            </li>
            <li>
              <strong>Emergency Services (Police, Fire, Ambulance):</strong> 911
            </li>
            <li>
              <strong>Crisis Text Line:</strong> Text HOME to 741741
            </li>
            <li>
              <strong>Emergency Email:</strong> help@emergencyservices.org
            </li>
          </ul>
          <p>
            Don't hesitate to contact a trusted friend, family member, or
            healthcare professional for support.
          </p>
        </div>
      );
    }

    setModalTitle(tool);
    setModalContent(content);
    setModalOpen(true);
  };

  useEffect(() => {
    // Format date: "Monday, July 22, 2025"
    const today = new Date();
    const options = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    setCurrentDate(today.toLocaleDateString(undefined, options));

    // First get user's location by IP
    fetch("http://ip-api.com/json/")
      .then((res) => res.json())
      .then((locationData) => {
        const { lat, lon } = locationData;

        // Then get weather from OpenWeatherMap
        fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=ca18712345ec04950bb79b1f38d42205&units=imperial`
        )
          .then((res) => res.json())
          .then((weatherData) => {
            if (weatherData.main && weatherData.main.temp) {
              setTemperature(`${Math.round(weatherData.main.temp)}°F`);
            }
          })
          .catch((err) => console.error("Weather fetch error:", err));
      })
      .catch((err) => console.error("Location fetch error:", err));
  }, []);

  return (
    <div className="dashboard-container">
      <button
        className="analyze-btn"
        style={{
          width: "auto",
          padding: "0.5em 1em",
          textAlign: "center",
          display: "inline-block",
          marginBottom: "1rem",
        }}
        onClick={handleLogout}
      >
        Logout
      </button>
      {/* Welcome Section */}
      <div className="welcome-card">
        <div>
          <h2>Welcome back, {username}!</h2>
          <p>{currentDate}</p>
        </div>
        <div className="temperature">{temperature || "Loading..."}</div>
      </div>

      {/* Stat Cards */}
      <div className="stats-row">
        {dashboardSummary.enough_data ? (
          <>
            <Card
              title="Weekly Average"
              value={`${(dashboardSummary.weekly_average * 100).toFixed(2)}%`}
              subtext="Last 7 days"
              icon={<TrendingUp size={20} color="#2487eb" />}
            />
            <Card
              title="Current Streak"
              value={`${dashboardSummary.current_streak} days`}
              subtext="Keep it up!"
              icon={<Target size={20} color="#2487eb" />}
            />
            <Card
              title="Total Check-ins"
              value={dashboardSummary.total_checkins}
              subtext="Last 30 days"
              icon={<Calendar size={20} color="#2487eb" />}
            />
            <Card
              title="Wellness Score"
              value={dashboardSummary.wellness_score}
              subtext="Based on stress"
              icon={<Award size={20} color="#2487eb" />}
            />
          </>
        ) : (
          <div
            style={{
              width: "100%",
              textAlign: "center",
              color: "red",
              padding: "1rem",
            }}
          >
            Please log at least 10 check-ins to view your wellness stats.
          </div>
        )}
      </div>

      {/* Stress Level Section */}
      <div className="stress-section">
        <div
          className="stress-header"
          style={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <User size={20} color="#2487eb" style={{ flexShrink: 0 }} />
            <h3>Today's Stress Level</h3>
          </div>
          <span className="stress-status">
            {roundedStress !== null
              ? roundedStress <= 3
                ? "Calm"
                : roundedStress <= 6
                ? "Moderate Stress"
                : "High Stress"
              : "No data"}
          </span>
        </div>
        <div className="stress-bar">
          <div
            className="stress-fill"
            style={{ width: `${stressPercent}%` }}
          ></div>
        </div>
        <div className="stress-labels">
          <span>Calm</span>
          <span>{`${stressPercent.toFixed(0)}%`}</span>
          <span>Stressed</span>
        </div>
      </div>

      {/* Check-in and Tools */}
      <div className="bottom-section">
        {/* Left: Check-in */}
        <div className="checkin-card">
          <div
            className="checkin-header"
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <Calendar size={20} color="#2487eb" />
            <h3 style={{ margin: 0 }}>Daily Check-in</h3>
          </div>
          <p>Monday, July 22, 2025</p>

          <p className="section-title">How was your day?</p>
          <TextBox
            type="textarea"
            placeholder="Write about your day..."
            name="dayEntry"
            value={dayEntry}
            onChange={(e) => setDayEntry(e.target.value)}
          />

          <p className="section-title">
            Overall Mood:
            {mood <= 20
              ? "Very Stressed"
              : mood <= 80
              ? "Neutral"
              : "Excellent"}
          </p>
          <input
            type="range"
            min="0"
            max="100"
            className="mood-slider"
            value={mood}
            onChange={(e) => setMood(Number(e.target.value))}
          />

          <div className="mood-labels">
            <span>Very Stressed</span>
            <span>Excellent</span>
          </div>

          <p className="section-title">What affected your day? (optional)</p>
          <div className="tag-container">
            {availableTags.map((tag) => (
              <span
                key={tag}
                className={`tag ${tags.includes(tag) ? "selected" : ""}`}
                onClick={() => toggleTag(tag)}
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  backgroundColor: tags.includes(tag) ? "#007bff" : "#e0e0e0",
                  color: tags.includes(tag) ? "white" : "black",
                  padding: "5px 10px",
                  marginRight: "8px",
                  borderRadius: "12px",
                  display: "inline-block",
                }}
              >
                {tag}
              </span>
            ))}
          </div>

          <button
            className="analyze-btn"
            onClick={handleAnalyze}
            disabled={loading || !dayEntry.trim()}
            style={{ marginTop: "1rem" }}
          >
            {loading ? "Analyzing..." : "Analyze My Day"}
          </button>
        </div>

        {/* Right: Tools */}
        <div className="tools-card">
          <h3 className="section-title">Quick Wellness Tools</h3>
          <div className="tools-grid">
            <ToolCard
              icon={Wind}
              title="Breathing Exercises"
              subtitle="5-minute guided breathing"
              onClick={() => openToolModal("Breathing Exercises")}
            />
            <ToolCard
              icon={Heart}
              title="Quick Meditation"
              subtitle="3-minute mindfulness"
              onClick={() => openToolModal("Quick Meditation")}
            />
            <ToolCard
              icon={BookOpen}
              title="Stress Tips"
              subtitle="Personalized Advice"
              onClick={() => openToolModal("Stress Tips")}
            />
            <ToolCard
              icon={Phone}
              title="Emergency Help"
              subtitle="Crisis resources"
              onClick={() => openToolModal("Emergency Help")}
            />
          </div>
        </div>
        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          title={modalTitle}
          content={modalContent}
        />
      </div>

      {/* Final Info Box */}
      <div className="info-card">
        <AlertCircle className="info-icon" />
        <h3>Analysis Result</h3>
        {roundedStress === null ? (
          <p>
            Fill out your daily check-in above to see your stress analysis and
            personalized recommendations.
          </p>
        ) : typeof analysisResult === "string" ? (
          <p>{analysisResult}</p> // error or message
        ) : (
          <p>Your predicted stress level is: {roundedStress}</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
